// Shared date-range filtering for flight/booking history views, so every list
// can default to a recent window and keep full history one tap away.
// Flight dates are 'YYYY-MM-DD' strings.

export type DateRange = 'this_month' | 'last_month' | 'last_90' | 'all';

export const DATE_RANGES: DateRange[] = ['this_month', 'last_month', 'last_90', 'all'];

export const RANGE_LABELS: Record<DateRange, string> = {
  this_month: 'This month',
  last_month: 'Last month',
  last_90: 'Last 90 days',
  all: 'All',
};

// Local YYYY-MM month key (avoids the UTC drift of toISOString at month edges).
function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function inDateRange(dateStr: string, range: DateRange, now: Date = new Date()): boolean {
  if (range === 'all') return true;
  if (!dateStr) return false;
  if (range === 'this_month') return dateStr.slice(0, 7) === monthKey(now);
  if (range === 'last_month') {
    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return dateStr.slice(0, 7) === monthKey(lm);
  }
  // last_90
  const d = new Date(`${dateStr}T12:00:00`);
  const cutoff = new Date(now.getTime() - 90 * 86400000);
  return d.getTime() >= cutoff.getTime();
}

/**
 * Today's date as the pilot sees it on the wall, YYYY-MM-DD.
 *
 * NOT `new Date().toISOString().slice(0,10)` — that is UTC, so every flight logged
 * after ~5pm Pacific was stamped with TOMORROW's date. Steven's 2 Sep evening flight
 * was recorded as 3 Sep. It matters in three places: the date on the record, which
 * billing cycle the flight lands in, and whether a flight matches the booking it was
 * flown under (see lib/utilization.ts — a misdated flight reads as a no-show).
 */
export function localToday(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}
